// shadcn composition of Radix AlertDialog; destructive actions only.
import { useRef } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { useTranslation } from "react-i18next";
import { Button } from "./button";

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  pending = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const { t } = useTranslation("ui");
  const wasOpen = useRef(false);
  const trigger = useRef<HTMLElement | null>(null);
  if (open && !wasOpen.current && typeof document !== "undefined")
    trigger.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  wasOpen.current = open;
  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!pending) onOpenChange(value);
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="vl-dialog-overlay" />
        <AlertDialog.Content
          className="vl-dialog"
          onCloseAutoFocus={(event) => {
            if (trigger.current?.isConnected) {
              event.preventDefault();
              trigger.current.focus();
            }
          }}
        >
          <AlertDialog.Title className="vl-section-title">
            {title}
          </AlertDialog.Title>
          <AlertDialog.Description className="vl-muted">
            {description}
          </AlertDialog.Description>
          <div className="vl-actions">
            <AlertDialog.Cancel asChild>
              <Button variant="outline" disabled={pending}>
                {cancelLabel || t("cancel")}
              </Button>
            </AlertDialog.Cancel>
            <Button
              variant="destructive"
              disabled={pending}
              onClick={() => void onConfirm()}
            >
              {pending ? t("working") : confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
